import type { GridLayoutData, GridRow } from './grid-types';
import { getBlocksInRow } from './grid-row-management';
import { GRID_CONFIG } from './grid-types';

const HALF_WIDTH_THRESHOLD_RATIO = 1 / 3;
const HALF_WIDTH_SPAN = GRID_CONFIG.COLUMNS / 2;

/**
 * Supported layout modes derived from the rendered grid container width.
 */
export type ResponsiveGridMode = 'full' | 'paired' | 'stacked';

/**
 * Options used to derive a responsive grid layout from the authored layout data.
 */
interface ResponsiveGridLayoutOptions {
	/**
	 * Target responsive mode for the derived layout.
	 */
	mode: ResponsiveGridMode;
}

/**
 * Derives the grid layout for the requested responsive mode.
 */
export function getResponsiveGridLayout(
	gridData: GridLayoutData,
	options: ResponsiveGridLayoutOptions,
): GridLayoutData {
	if (options.mode === 'full') {
		return gridData;
	}

	if (options.mode === 'paired') {
		return derivePairedGridLayout(gridData);
	}

	return deriveStackedGridLayout(gridData);
}

/**
 * Converts the authored layout into the paired layout, keeping narrow consecutive blocks side by side.
 */
export function derivePairedGridLayout(gridData: GridLayoutData): GridLayoutData {
	const rows: GridRow[] = [];
	const blockPositions: GridLayoutData['blockPositions'] = {};
	let nextRowIndex = 0;

	for (const [sourceRowIndex, sourceRow] of gridData.rows.entries()) {
		const rowBlocks = getBlocksInRow(sourceRowIndex, gridData.blockPositions);
		let pendingHalfWidthBlock: (typeof rowBlocks)[number] | null = null;

		for (const block of rowBlocks) {
			if (!isHalfWidthCandidate(block.pos.colSpan, sourceRow.columns)) {
				if (pendingHalfWidthBlock) {
					addFullWidthRow(pendingHalfWidthBlock.id);
					pendingHalfWidthBlock = null;
				}

				addFullWidthRow(block.id);
				continue;
			}

			if (!pendingHalfWidthBlock) {
				pendingHalfWidthBlock = block;
				continue;
			}

			rows.push({ columns: GRID_CONFIG.COLUMNS });

			blockPositions[pendingHalfWidthBlock.id] = {
				rowIndex: nextRowIndex,
				colStart: 0,
				colSpan: HALF_WIDTH_SPAN,
			};

			blockPositions[block.id] = {
				rowIndex: nextRowIndex,
				colStart: HALF_WIDTH_SPAN,
				colSpan: HALF_WIDTH_SPAN,
			};

			nextRowIndex++;
			pendingHalfWidthBlock = null;
		}

		if (pendingHalfWidthBlock) {
			addFullWidthRow(pendingHalfWidthBlock.id);
		}
	}

	return {
		rows,
		blockPositions,
	};

	/**
	 * Adds a full-width row for a block that cannot be paired.
	 */
	function addFullWidthRow(childId: string) {
		rows.push({ columns: GRID_CONFIG.COLUMNS });

		blockPositions[childId] = {
			rowIndex: nextRowIndex,
			colStart: 0,
			colSpan: GRID_CONFIG.COLUMNS,
		};

		nextRowIndex++;
	}
}

/**
 * Converts every block into its own full-width row in visual order.
 */
export function deriveStackedGridLayout(gridData: GridLayoutData): GridLayoutData {
	const rows: GridRow[] = [];
	const blockPositions: GridLayoutData['blockPositions'] = {};
	const orderedBlocks = getBlocksInVisualOrder(gridData);

	for (const [rowIndex, block] of orderedBlocks.entries()) {
		rows.push({ columns: GRID_CONFIG.COLUMNS });

		blockPositions[block.id] = {
			rowIndex,
			colStart: 0,
			colSpan: GRID_CONFIG.COLUMNS,
		};
	}

	return {
		rows,
		blockPositions,
	};
}

/**
 * Returns whether a block is narrow enough to pair with another block.
 */
function isHalfWidthCandidate(colSpan: number, rowColumns: number): boolean {
	return colSpan <= rowColumns * HALF_WIDTH_THRESHOLD_RATIO;
}

/**
 * Reads blocks in row and column order from the stored grid data.
 */
function getBlocksInVisualOrder(gridData: GridLayoutData) {
	return gridData.rows.flatMap((_row, rowIndex) => {
		return getBlocksInRow(rowIndex, gridData.blockPositions);
	});
}
